import corsiaImg from '../../assets/images/compliance-corsia.jpg';

export interface TopicPage {
  key: string;
  label: string;
  description: string;
  aliases: string[];
  to: string;
  image: string;
  imageLabel: string;
}

// Topic pages promoted as a card in Research hub search results.
export const TOPIC_PAGES: TopicPage[] = [
  {
    key: 'corsia',
    label: 'CORSIA compliance & reporting',
    description:
      "Visit our dedicated CORSIA page for the latest on ICAO's Carbon Offsetting and Reduction Scheme for International Aviation.",
    aliases: ['corsia', 'carbon offsetting and reduction scheme for international aviation'],
    to: '/research-hub/corsia',
    image: corsiaImg,
    imageLabel: 'CORSIA',
  },
];

const normalise = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ');

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Whole-word so "corsia phase 1" matches but "corsian" doesn't.
const containsWord = (haystack: string, needle: string) =>
  new RegExp(`(^|\\W)${escapeRegExp(needle)}($|\\W)`).test(haystack);

export const findTopicPage = (query: string): TopicPage | undefined => {
  const normalised = normalise(query);
  if (!normalised) return undefined;
  return TOPIC_PAGES.find((topic) =>
    topic.aliases.some((alias) => containsWord(normalised, normalise(alias)))
  );
};

const escapeHtml = (value: string) =>
  value.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));

// Marks every occurrence of the search term so the card renders it through the
// same <mark> styling Typesense highlights use. Only slices of `text` are ever
// emitted — the term itself is never interpolated into the HTML.
const markTerm = (text: string, term: string) => {
  const needle = term.trim().toLowerCase();
  if (!needle) return escapeHtml(text);
  const haystack = text.toLowerCase();
  let out = '';
  let from = 0;
  for (let at = haystack.indexOf(needle); at !== -1; at = haystack.indexOf(needle, from)) {
    out += `${escapeHtml(text.slice(from, at))}<mark>${escapeHtml(
      text.slice(at, at + needle.length)
    )}</mark>`;
    from = at + needle.length;
  }
  return out + escapeHtml(text.slice(from));
};

// A broader query ("corsia phase 1") won't appear verbatim in the description,
// so fall back to marking the alias that matched.
const highlightTerm = (topic: TopicPage, query: string) => {
  const description = normalise(topic.description);
  if (description.includes(normalise(query))) return query;
  return topic.aliases.find((alias) => description.includes(normalise(alias))) ?? query;
};

export const toTopicPageHit = (topic: TopicPage, query: string) => ({
  document: {
    id: `topic_page_${topic.key}`,
    type: 'topic_page',
    title: topic.description,
    category_display_name: topic.label,
    cover_url: topic.image,
    image_label: topic.imageLabel,
    topic_to: topic.to,
  },
  highlight: { title: { snippet: markTerm(topic.description, highlightTerm(topic, query)) } },
});
